import React, { useState, useEffect } from 'react';
import { MessageSquare, Pointer, Users, X } from 'lucide-react';
import './PodTodayOnboarding.css';

interface PodTodayOnboardingProps {
  podName: string;
  planTitle: string;
  onDismiss: () => void;
}

const STORAGE_KEY = 'vizday_pod_today_onboarded';

function hasSeenOnboarding(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function markOnboarded(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch { /* */ }
}

const SCREENS = [
  {
    Icon: Users,
    title: 'This is your Pod Today',
    getDesc: (podName: string, planTitle: string) =>
      `${podName} is on ${planTitle} together. Everyone sees the same view — changes show up for everyone, instantly.`,
  },
  {
    Icon: Pointer,
    title: 'Tap your avatar to toggle',
    getDesc: () =>
      'Each activity shows who\'s in. Tap your avatar to opt out of something, or tap again to rejoin.',
  },
  {
    Icon: MessageSquare,
    title: 'Post updates as you go',
    getDesc: () =>
      'Running late? Change of plans? Tap the update button on any activity to let everyone know.',
  },
];

const PodTodayOnboarding: React.FC<PodTodayOnboardingProps> = ({
  podName, planTitle, onDismiss,
}) => {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!hasSeenOnboarding()) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const screen = SCREENS[step];

  const handleNext = () => {
    if (step < SCREENS.length - 1) {
      setStep(step + 1);
    } else {
      markOnboarded();
      setVisible(false);
      onDismiss();
    }
  };

  const handleSkip = () => {
    markOnboarded();
    setVisible(false);
    onDismiss();
  };

  return (
    <div className="pto-overlay">
      <div className="pto-card">
        <button className="pto-close" onClick={handleSkip}><X size={16} /></button>

        <div className="pto-dots">
          {SCREENS.map((_, i) => (
            <span key={i} className={`pto-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`} />
          ))}
        </div>

        <span className="pto-emoji"><screen.Icon size={36} aria-hidden="true" /></span>
        <h3 className="pto-title">{screen.title}</h3>
        <p className="pto-desc">{screen.getDesc(podName, planTitle)}</p>

        <button className="pto-next" onClick={handleNext}>
          {step === SCREENS.length - 1 ? 'Got it' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default PodTodayOnboarding;
